import { TRAINING_PAY_STATUSES, type TrainingPayStatus, type WageSettingsTarget } from "../../components/team/WageSettings/deepLink";
import type { NurseTrainingStatus, OvertimeKind } from "../models";

/*
 * The wage breakdown's `rateType` labels, as `backend/services/cost_calculation/calcaulte_cost_logic.py`
 * writes them. A paid segment is labelled with the NAME of the hours-rate rule that priced it (the
 * `shift_name` from `calculate_hours_rate_boundary_data`), or "" when no rule matched; the rest are
 * the literals and `_build_ot_label` output below.
 */

/** An unpaid break, deducted by a break rule: `base`, `rate` and `wage` are all 0. */
export const BREAK_RATE_TYPE = "Break";

/** A per-shift bonus row. Set on the shift itself, not by a Wage Settings rule. */
export const BONUS_RATE_TYPE = "Bonus";

/** Holiday pay. Its rate lives in support-only Settings, not on Wage Settings. */
export const HOLIDAY_RATE_TYPE = "Holiday";

/** A salaried nurse's single row (`calculate_nurse_wage`'s annual branch): their own pay, no rule applies. */
export const SALARIED_RATE_TYPE = "Hourly";

/**
 * `_build_ot_label` always ends in "OT": "{rule name | Holiday} {Daily | Weekly | Consecutive} OT", with
 * either part absent — down to a bare "OT".
 */
const OVERTIME_RATE_TYPE = "OT";
const OVERTIME_RATE_TYPE_SUFFIX = ` ${OVERTIME_RATE_TYPE}`;

/** `_OT_TYPE_LABELS`: the qualifier each overtime kind writes just before "OT". */
const OVERTIME_KIND_BY_QUALIFIER: Readonly<Record<string, OvertimeKind>> = {
	Daily: "daily",
	Weekly: "weekly",
	Consecutive: "consecutive",
};

const isOvertimeRateType = (rateType: string): boolean => rateType === OVERTIME_RATE_TYPE || rateType.endsWith(OVERTIME_RATE_TYPE_SUFFIX);

/**
 * The overtime kind an OT label names, read from the word just before "OT" — the qualifier always sits
 * there, after any rule name, so a rule called "Weekly Night" still reads by its qualifier. `undefined`
 * when there is none: a bare "OT", "Holiday OT", or "Day OT" (a rule name with no qualifier).
 */
const overtimeKindOf = (label: string): OvertimeKind | undefined => {
	const words = label.slice(0, -OVERTIME_RATE_TYPE.length).trim().split(/\s+/);
	const qualifier = words[words.length - 1];
	return Object.hasOwn(OVERTIME_KIND_BY_QUALIFIER, qualifier) ? OVERTIME_KIND_BY_QUALIFIER[qualifier] : undefined;
};

/**
 * Where on Team → Wage Settings a wage breakdown row's rate type is configured, or `undefined` when it
 * is not configured there at all (holiday, bonus, a salaried row, an unlabelled segment) and so must not
 * be a link.
 *
 * An hours-rate row's label IS the rule's name, so it asks for that row. An overtime label carries only
 * the KIND of overtime, so it asks for every rule of that kind. A break label carries nothing: Break
 * rules decides from its own rows whether one of them is unambiguously "the" rule.
 */
export const rateTypeTarget = (rateType: string): WageSettingsTarget | undefined => {
	const label = rateType.trim();
	if (label === "" || label === BONUS_RATE_TYPE || label === HOLIDAY_RATE_TYPE || label === SALARIED_RATE_TYPE) return undefined;
	if (label === BREAK_RATE_TYPE) return { section: "break-rules" };
	// Checked before the hours-rate fallthrough: "Night Daily OT" starts with a rule name but is overtime.
	if (isOvertimeRateType(label)) {
		const overtimeKind = overtimeKindOf(label);
		return overtimeKind === undefined ? { section: "overtime" } : { section: "overtime", overtimeKind };
	}
	return { section: "hours-rate", rule: label };
};

/** A link to one Training & orientation card: always names its card. */
export type TrainingPayTarget = { section: "training-orientation"; trainingStatus: TrainingPayStatus };

const isTrainingPayStatus = (status: NurseTrainingStatus): status is TrainingPayStatus => TRAINING_PAY_STATUSES.some((payStatus) => payStatus === status);

/**
 * Where the base wage of a shift priced under `trainingStatus` is set, or `undefined` when it is the
 * nurse's own wage. Trainee and Orientation replace the base with the department's training/orientation
 * rate before any row is built, so no rate-type label says so; the breakdown links its base instead.
 */
export const trainingPayTarget = (trainingStatus: NurseTrainingStatus): TrainingPayTarget | undefined =>
	isTrainingPayStatus(trainingStatus) ? { section: "training-orientation", trainingStatus } : undefined;
